import React, { useRef, useEffect, useState } from "react";
import {
 FaChevronLeft,
 FaChevronRight,
 FaEnvelope,
 FaPhoneAlt,
 FaCheckCircle,
  FaHeart,
} from "react-icons/fa";
import {
 LuBook,
 LuCalendar,
 LuLanguages,
 LuMail,
 LuPhone,
 LuRuler,
 LuWallet,
 LuMapPin,
 LuUsers,
 LuBriefcase,
 LuGraduationCap
} from "react-icons/lu";
import { baseurl, imgurl } from "../baseurl";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { getStoredToken } from "../component/utils/getStoredToken";
import ChatButton from "../component/ChatButton";
import CompatibilityCard from "../component/CompatibilityCard";
import InterestButton from "../component/InterestButton";
import FooterTwo from "../component/FooterTwo";

function ViewProfileMobile() {
 const token = getStoredToken();
 const { uid } = useParams();
 const navigate = useNavigate();

 const [profile, setProfile] = useState(null);
 const [compatibility, setCompatibility] = useState([]);
 const [loading, setLoading] = useState(true);

 const galleryRef = useRef(null);
 const [galleryImages, setGalleryImages] = useState([]);
 const [currentImageIndex, setCurrentImageIndex] = useState(0);

 const [lightboxOpen, setLightboxOpen] = useState(false);
 const [currentImage, setCurrentImage] = useState(null);

 // Logged-in user state (needed for CompatibilityCard logic)
 const [loggedUser, setLoggedUser] = useState(null);

 const openLightbox = (img) => {
  setCurrentImage(img);
  setLightboxOpen(true);
 };

 const closeLightbox = () => {
  setLightboxOpen(false);
  setCurrentImage(null);
 };

 const scrollGallery = (dir) => {
  const newIndex =
   dir === "right"
    ? Math.min(currentImageIndex + 1, galleryImages.length - 1)
    : Math.max(currentImageIndex - 1, 0);

  setCurrentImageIndex(newIndex);
  if (galleryRef.current) {
   // Calculate scroll position based on index and item width (assuming 100% width + gap)
   const itemWidth = galleryRef.current.clientWidth; 
   galleryRef.current.scrollTo({
    left: newIndex * itemWidth,
    behavior: "smooth",
   });
  }
 };

 /* --------------------------
   FETCH PROFILE + LOGGED USER
 --------------------------- */
 useEffect(() => {
  const fetchProfile = async () => {
   if (!token || !uid) {
        setLoading(false);
        return;
      }
      setLoading(true);

   try {
    const res = await axios.get(`${baseurl}getUserByUid/${uid}`, {
     headers: { Authorization: `Bearer ${token}` },
    });

    if (res.data.success) {
     /* ------------------------------
       1️⃣ VIEWED USER ("user")
     ------------------------------- */
     if (res.data.user) {
      const u = res.data.user;

      setProfile({
       name: `${u.first_name} ${u.last_name}`,
       age: u.date_of_birth
        ? Math.floor(
          (new Date() - new Date(u.date_of_birth)) /
           (1000 * 60 * 60 * 24 * 365)
         )
        : "N/A",
       height: u.height
        ? `${(parseFloat(u.height) / 30.48).toFixed(1)}'`
        : "N/A",
       city: u.location ? u.location.split(',')[0] : "N/A", // Only city
       display_id: u.display_id,
       profession: u.profession || "N/A",
       education: u.degree || u.highest_qualification || "N/A",
       current_occupation: u.current_occupation || "N/A",
       annual_income: u.annual_income || "N/A",
       about: u.about || "No information provided about myself.",
       marital_status: u.marital_status || "N/A",
       location: u.location || "N/A",
       religion: u.religion || "N/A",
       caste: u.caste || "N/A",
       sect: u.sect || "N/A",
       mother_tongue: u.mother_tongue || "N/A",
              smoking_habits: u.smoking_habits || "N/A",
              eating_habits: u.eating_habits || "N/A",
              drinking_habits: u.drinking_habits || "N/A",
       family: {
        father: u.father_profession || "N/A",
        mother: u.mother_profession || "N/A",
        siblings: `${u.number_of_siblings || 0} siblings (${
         u.married_siblings || 0
        } married)`,
        family_type: u.family_type || "N/A", // Added family type
       },
       contact: {
        phone: u.phone,
        email: u.email,
       },
       image: u.main_photo ? `${imgurl}${u.main_photo}` : null,
      });

      setGalleryImages(
       u.photos?.length
        ? u.photos.map((p) => `${imgurl}${p}`)
        : u.main_photo
        ? [`${imgurl}${u.main_photo}`]
        : []
      );
     }

     /* ------------------------------
       2️⃣ LOGGED USER ("logged_user")
     ------------------------------- */
     if (res.data.logged_user) {
      const lu = res.data.logged_user;

      setLoggedUser({
       photos: lu.photos || [],
      });
     }

     /* ------------------------------
       3️⃣ COMPATIBILITY
     ------------------------------- */
     if (res.data.compatibility) {
      const comp = Object.entries(res.data.compatibility)
       .filter(([key]) => key !== "overall")
       .map(([key, value]) => ({
        title: key
         .replace(/_/g, " ")
         .replace(/\b\w/g, (c) => c.toUpperCase()),
        score: value.score,
        reason: value.reason,
       }));

      setCompatibility(comp);
     }
    }
   } catch (err) {
    console.error("Error fetching user profile:", err);
   } finally {
        setLoading(false);
      }
  };

  fetchProfile();
 }, [token, uid]);

 /* ----------------------------
   MASKING PHONE + EMAIL
 ----------------------------- */
 const maskPhone = (phone) => {
  if (!phone) return "N/A";
  const last2 = phone.slice(-2);
  return "XXXXXXXX" + last2;
 };

 const maskEmail = (email) => {
  if (!email) return "N/A";
  const [u, domain] = email.split("@");
  const maskedUser =
   u.length > 2 ? u[0] + "*".repeat(u.length - 2) + u.slice(-1) : u[0] + "*";

  const [dName, dExt] = domain.split(".");
  const maskedDomain =
   dName.length > 2
    ? dName[0] + "*".repeat(dName.length - 2) + dName.slice(-1)
    : dName[0] + "*";

  return `${maskedUser}@${maskedDomain}.${dExt}`;
 };

 if (loading) return <p className="text-center py-5">Loading...</p>;
  if (!profile) return <p className="text-center py-5">Profile not found or access denied.</p>;

 /* ----------------------------
   UI RENDER STARTS HERE
 ----------------------------- */
 return (
  <>
      {/* --------------------
      // Mobile-specific CSS
      // -------------------- */}
   <style>{`
    .mobile-profile-app {
      max-width: 500px; /* Standard mobile width constraint */
      margin: 0 auto;
      background-color: #f8f9fa; /* Light grey background */
      min-height: 100vh;
      padding-bottom: 70px; /* Space for fixed action bar */
    }

        /* --- Image Gallery/Header --- */
    .gallery-container {
      position: relative;
      height: 530px; /* Fixed height for image area */
      overflow: hidden;
    }

    .gallery-scroll-area {
      display: flex;
      overflow-x: scroll;
      scroll-snap-type: x mandatory;
      -webkit-overflow-scrolling: touch;
      height: 100%;
      /* Hide scrollbar */
      &::-webkit-scrollbar { display: none; }
      -ms-overflow-style: none; /* IE and Edge */
      scrollbar-width: none; /* Firefox */
    }

    .gallery-item {
      flex: 0 0 100%; /* Each item takes full width */
      scroll-snap-align: start;
      height: 100%;
    }
    .gallery-item img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .gallery-nav-btn {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      background: rgba(0, 0, 0, 0.5);
      color: white;
      border: none;
      padding: 10px;
      z-index: 10;
      cursor: pointer;
      border-radius: 50%;
      transition: background 0.3s;
    }
    .gallery-nav-btn:hover { background: rgba(0, 0, 0, 0.7); }
    .gallery-nav-btn.left { left: 10px; }
    .gallery-nav-btn.right { right: 10px; }
        .gallery-nav-btn:disabled { opacity: 0.5; cursor: default; }

        .image-indicator {
            position: absolute;
            top: 15px;
            right: 15px;
            background: rgba(0, 0, 0, 0.6);
            color: white;
            padding: 5px 10px;
            border-radius: 20px;
            font-size: 0.8rem;
            z-index: 10;
        }

        /* --- Main Profile Header/Details --- */
        .profile-details-mobile {
            position: absolute;
            bottom: 0;
            left: 0;
            width: 100%;
            padding: 20px 15px;
            color: #fff;
            // background: linear-gradient(to top, rgba(0, 0, 0, 0.85), rgba(0, 0, 0, 0));
            background: linear-gradient(to top, rgb(0 0 0), rgb(0 0 0 / 78%));
            z-index: 5;
        }
        .profile-details-mobile h2 {
            font-size: 1.8rem;
            font-weight: 700;
            margin-bottom: 5px;
        }
        .profile-details-mobile p {
            margin-bottom: 5px;
            font-size: 1rem;
        }
        .profile-details-mobile .badge-pill {
            display: inline-flex;
            align-items: center;
            background: rgba(255, 255, 255, 0.2);
            padding: 5px 10px;
            border-radius: 15px;
            font-size: 0.8rem;
            margin-right: 5px;
            margin-top: 5px;
        }
        .profile-details-mobile .badge-pill svg {
            margin-right: 4px;
        }
        .verification-badge {
            background-color: #2ecc71; /* Green */
            color: white;
            font-size: 0.9rem;
            padding: 4px 8px;
            border-radius: 5px;
            margin-left: 10px;
        }

        /* --- Section Styling --- */
        .profile-section-mobile {
            background: white;
            margin: 10px 0;
            padding: 15px;
            border-radius: 10px;
            box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
        }
        .section-title {
            font-size: 1.2rem;
            font-weight: 700;
            color: #a12354; /* Theme Color */
            margin-bottom: 15px;
            border-bottom: 2px solid #eee;
            padding-bottom: 8px;
        }
        .detail-item {
            display: flex;
            align-items: flex-start;
            padding: 8px 0;
            font-size: 0.95rem;
            color: #333;
        }
        .detail-icon {
            color: #a12354; /* Icon Color */
            margin-right: 10px;
            min-width: 25px;
        }
        .detail-label {
            font-weight: 500;
            color: #777;
            min-width: 100px;
        }
        .detail-value {
            font-weight: 600;
            color: #333;
            flex-grow: 1;
            text-align: right;
            text-transform: capitalize;
        }

        /* --- Family Timeline Adjustments for Mobile --- */
        .family-timeline-card {
            padding: 15px !important;
        }
        .timeline {
            border-left: 2px solid #eee;
            padding-left: 10px;
            margin-left: 5px;
        }
        .timeline-item {
            position: relative;
            padding-bottom: 15px;
            margin-bottom: 15px;
        }
        .timeline-dot {
            position: absolute;
            left: -16px; /* Adjust based on border size */
            top: 5px;
            width: 10px;
            height: 10px;
            background-color: #d84848;
            border-radius: 50%;
            border: 3px solid #f8f9fa; /* Match background */
        }
        .timeline-label {
            font-weight: 700;
            color: #333;
            margin-bottom: 5px;
            font-size: 0.95rem;
        }
        .family-detail-value {
             font-size: 0.9rem;
             color: #555;
             margin-left: 10px;
             text-transform: capitalize;
        }

        /* --- Contact Section --- */
        .contact-info-card {
            background-color: #fcebeb; /* Light Red background */
            border-left: 5px solid #d84848;
        }
        .contact-info-card .detail-icon {
            color: #d84848;
        }

        /* --- Fixed Action Bar --- */
        .fixed-action-bar {
            position: fixed;
            bottom: 0;
            left: 0;
            right: 0;
            max-width: 500px;
            margin: 0 auto;
            background: white;
            box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
            padding: 10px 15px;
            z-index: 1000;
            display: flex;
            justify-content: space-between;
            gap: 10px;
        }
        .fixed-action-bar .btn {
            flex: 1;
            padding: 12px 10px;
            font-weight: 600;
            font-size: 1rem;
            border-radius: 8px;
        }
        .fixed-action-bar .btn-interest {
            background-color: #d84848; /* Theme Red */
            color: white;
            border: none;
        }
        .fixed-action-bar .btn-chat {
            background-color: #3498db; /* Theme Blue */
            color: white;
            border: none;
        }

        /* --- Lightbox CSS --- */
        .lightbox {
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(0, 0, 0, 0.9);
            z-index: 2000;
            display: flex;
            align-items: center;
            justify-content: center;
        }
        .lightbox-content {
            max-width: 90%;
            max-height: 90%;
            display: block;
        }
   `}</style>

      {/* --------------------
      // Main Mobile View Container
      // -------------------- */}
   <div className="mobile-profile-app"> 
        {/* 1. Image Gallery Header */}
        <div className="gallery-container">
            <div 
                className="gallery-scroll-area" 
                ref={galleryRef}
                onScroll={(e) => {
                    // Update index on manual scroll for indicator
                    const width = e.target.clientWidth;
                    const index = Math.round(e.target.scrollLeft / width);
                    setCurrentImageIndex(index);
                }}
            >
                {galleryImages.length > 0 ? (
                    galleryImages.map((img, index) => (
                        <div key={index} className="gallery-item" onClick={() => openLightbox(img)}>
                            <img src={img} alt={`${profile.name} ${index + 1}`} style={{width:"100%", height: "60vh", objectFit: "contain",
                                background: "#161616ff"
                            }} />
                        </div>
                    ))
                ) : (
                    <div className="gallery-item d-flex align-items-center justify-content-center bg-secondary text-white">
                         <p>No Photos Available</p>
                    </div>
                )}
            </div>

            {/* Navigation Buttons (Optional, but good for UX) */}
            {galleryImages.length > 1 && (
                <>
                    <button 
                        className="gallery-nav-btn left" 
                        onClick={() => scrollGallery("left")}
                        disabled={currentImageIndex === 0}
                    >
                        <FaChevronLeft />
                    </button>
                    <button 
                        className="gallery-nav-btn right" 
                        onClick={() => scrollGallery("right")}
                        disabled={currentImageIndex === galleryImages.length - 1}
                    >
                        <FaChevronRight />
                    </button>
                </>
            )}

            {/* Image Indicator */}
            {galleryImages.length > 0 && (
                 <div className="image-indicator">
                    {currentImageIndex + 1} / {galleryImages.length}
                </div>
            )}


            {/* Profile Overlay Details */}
            <div className="profile-details-mobile">
                <h2>
                    {profile.display_id}
                    <span className="verification-badge ms-2"><FaCheckCircle className="me-1"/> Verified</span>
                </h2>
                <p>
                    <LuCalendar className="me-1" /> {profile.age} yrs • 
                    <LuRuler className="ms-3 me-1" /> {profile.height} • 
                    <LuLanguages className="ms-3 me-1" /> {profile.mother_tongue}
                </p>
                
                <p>
                    <LuMapPin className="me-1" /> {profile.city} • 
                    <LuBook className="ms-3 me-1" /> {profile.religion} ({profile.sect})
                </p>
            </div>
        </div>

        {/* 2. About Me Section */}
        <div className="profile-section-mobile">
            <h3 className="section-title">About Me</h3>
            <p className="small fst-italic">"{profile.about}"</p>
        </div>

        {/* 3. Education & Career Section */}
        <div className="profile-section-mobile">
            <h3 className="section-title">Career & Education</h3>
            <div className="detail-item">
                <div className="detail-icon"><LuBriefcase size={20} /></div>
                <div className="detail-label">Profession</div>
                <div className="detail-value">{profile.current_occupation}</div>
            </div>
            <hr className="my-1"/>
            <div className="detail-item">
                <div className="detail-icon"><LuGraduationCap size={20} /></div>
                <div className="detail-label">Education</div>
                <div className="detail-value">{profile.education}</div>
            </div>
            <hr className="my-1"/>
            <div className="detail-item">
                <div className="detail-icon"><LuWallet size={20} /></div>
                <div className="detail-label">Income</div>
                <div className="detail-value">{profile.annual_income}</div>
            </div>
        </div>
        
        {/* 4. Lifestyle & Basic Info Section */}
        <div className="profile-section-mobile">
            <h3 className="section-title">Basic & Lifestyle</h3>
            <div className="detail-item">
                <div className="detail-icon"><FaHeart size={20} /></div>
                <div className="detail-label">Marital Status</div>
                <div className="detail-value">{profile.marital_status}</div>
            </div>
            <hr className="my-1"/>
            <div className="detail-item">
                <div className="detail-icon"><LuUsers size={20} /></div>
                <div className="detail-label">Caste</div>
                <div className="detail-value">{profile.caste}</div>
            </div>
            <hr className="my-1"/>
             <div className="detail-item">
                <div className="detail-icon"><LuBook size={20} /> </div>
                <div className="detail-label">Religion</div>
                <div className="detail-value">{profile.religion} ({profile.caste}) ({profile.sect})</div>
            </div>
            <hr className="my-1"/>
            <div className="detail-item">
                <div className="detail-icon"><LuLanguages size={20} /> </div>
                <div className="detail-label">Mother Tongue</div>
                <div className="detail-value">{profile.mother_tongue}</div>
            </div>
            {/* <hr className="my-1"/>
            <div className="detail-item">
                <div className="detail-icon">🍴</div>
                <div className="detail-label">Eating Habit</div>
                <div className="detail-value">{profile.eating_habits}</div>
            </div> */}
        </div>

        {/* 5. Family Section */}
        <div className="profile-section-mobile family-timeline-card">
            <h3 className="section-title">Family Background</h3>

            {/* Family Type */}
             {/* <div className="detail-item mb-3">
                <div className="detail-icon"><LuUsers size={20} /></div>
                <div className="detail-label">Family Type</div>
                <div className="detail-value">{profile.family.family_type || "N/A"}</div>
            </div> */}

            <div className="timeline">
                <div className="timeline-item">
                    <div className="timeline-dot"></div>
                    <div className="timeline-label">Father's Profession</div>
                    <div className="family-detail-value">{profile.family.father}</div>
                </div>

                <div className="timeline-item">
                    <div className="timeline-dot"></div>
                    <div className="timeline-label">Mother's Profession</div>
                    <div className="family-detail-value">{profile.family.mother}</div>
                </div>
                
                <div className="timeline-item">
                    <div className="timeline-dot"></div>
                    <div className="timeline-label">Siblings</div>
                    <div className="family-detail-value">{profile.family.siblings}</div>
                </div>
            </div>
        </div>
        
        {/* 6. Contact Section (Masked) */}
        <div className="profile-section-mobile contact-info-card">
            <h3 className="section-title">Contact Information</h3>
            <p className="text-danger small mb-3">
                {/* *Contact details are masked and viewable only after sending interest.* */}
            </p>
            <div className="detail-item">
                <div className="detail-icon"><LuPhone size={20} /></div>
                <div className="detail-label">Phone</div>
                <div className="detail-value text-dark">{maskPhone(profile.contact.phone)}</div>
            </div> 
            <hr className="my-1"/>
            <div className="detail-item">
                <div className="detail-icon"><LuMail size={20} /></div>
                <div className="detail-label">Email</div>
                <div className="detail-value text-dark">{maskEmail(profile.contact.email)}</div>
            </div> 
        </div>

        {/* 7. Compatibility Score (Using existing component) */}
        <div className="px-3">
            <CompatibilityCard
                compatibility={compatibility}
                loggeduser={loggedUser ? loggedUser.photos : []}
                user={galleryImages}
                uid={uid}
            />
        </div>

   </div>

        {/* 8. Fixed Action Bar (Always visible at the bottom) */}
        <div className="fixed-action-bar">
            {/* Using the original InterestButton component */}
            <InterestButton uid={uid} className="btn btn-interest" />
            
            {/* Using the original ChatButton component */}
            <ChatButton uid={uid} className="btn btn-chat" />
        </div>

      {/* Lightbox for full screen image viewing */}
      {lightboxOpen && (
        <div className="lightbox" onClick={closeLightbox}>
          <img src={currentImage} alt="Full view" className="lightbox-content" />
        </div>
      )}

  </>
 );
}

export default ViewProfileMobile;